import {
  useHttpClient,
  useNetworkStatus,
  useToast,
} from '@airship-platform/airship-patterns';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useReducer,
} from 'react';
import {
  ManualJAgentRequest,
  ManualJAgentResponse,
  ManualJCalculationResult,
  StructuredLoadInput,
} from './manualJAgent.models';
import {
  initState,
  manualJAgentReducer,
  ManualJAgentState,
} from './manualJAgent.reducer';

interface ManualJAgentContextProps {
  results: ManualJCalculationResult[];
  currentResult: ManualJCalculationResult | null;
  isLoading: boolean;
  error: string | null;
  calculateLoad: (
    request: ManualJAgentRequest,
    options?: { signal?: AbortSignal }
  ) => Promise<void>;
  calculateLoadStructured: (
    input: StructuredLoadInput,
    options?: { signal?: AbortSignal }
  ) => Promise<void>;
  clearCurrentResult: () => void;
}

const ManualJAgentContext = createContext<ManualJAgentContextProps | undefined>(
  undefined
);

type OwnProps = PropsWithChildren<{
  initialState?: Partial<ManualJAgentState>;
}>;

export const ManualJAgentProvider: React.FC<OwnProps> = ({
  children,
  initialState,
}) => {
  const [state, dispatch] = useReducer(manualJAgentReducer, {
    ...initState,
    ...initialState,
  });
  const isOnline = useNetworkStatus();
  const httpClient = useHttpClient();
  const { showToast } = useToast();

  useEffect(() => {
    state.error && showToast(state.error, 'error');
  }, [state.error, showToast]);

  const calculateLoad: ManualJAgentContextProps['calculateLoad'] = useCallback(
    async (request, options) => {
      if (!isOnline()) {
        dispatch({ type: 'SET_LOADING_STATE', payload: { isLoading: false } });
        return;
      }
      dispatch({ type: 'CALCULATE_LOAD_START' });

      try {
        const response = await httpClient.post<ManualJAgentResponse>(
          '/bedrock-agents/manual-j-agent/invoke',
          request,
          { signal: options?.signal }
        );

        const result: ManualJCalculationResult = {
          ...response,
          calculatedAt: new Date().toISOString(),
        };

        dispatch({
          type: 'CALCULATE_LOAD_SUCCESS',
          payload: { result },
        });
      } catch (error) {
        if (error instanceof Error && error.message === 'canceled') {
          dispatch({ type: 'CALCULATE_LOAD_CANCELLED' });
        } else {
          dispatch({
            type: 'CALCULATE_LOAD_FAILURE',
            payload: { error: `Manual J Agent calculation failed: ${error}` },
          });
        }
      }
    },
    [httpClient, isOnline]
  );

  const calculateLoadStructured: ManualJAgentContextProps['calculateLoadStructured'] =
    useCallback(
      async (input, options) => {
        const naturalLanguageInput = `Calculate HVAC load for a home with ${
          input.squareFootage
        } square feet, ${input.ceilingHeight} foot ceilings, ${
          input.people
        } people, ${input.windows} windows, ${
          input.doors
        } doors, ${input.insulation.toLowerCase()} insulation, ${input.sunlightExposure.toLowerCase()} sunlight exposure, ${input.sealLevel.toLowerCase()} air sealing${
          input.zipCode ? `, and zip code ${input.zipCode}` : ''
        }.`;

        return calculateLoad({ userInput: naturalLanguageInput }, options);
      },
      [calculateLoad]
    );

  const clearCurrentResult = useCallback(() => {
    dispatch({ type: 'CLEAR_CURRENT_RESULT' });
  }, []);

  return (
    <ManualJAgentContext.Provider
      value={{
        results: state.results,
        currentResult: state.currentResult,
        isLoading: state.isLoading,
        error: state.error,
        calculateLoad,
        calculateLoadStructured,
        clearCurrentResult,
      }}
    >
      {children}
    </ManualJAgentContext.Provider>
  );
};

export const useManualJAgent = () => {
  const context = useContext(ManualJAgentContext);
  if (context === undefined) {
    throw new Error(
      'useManualJAgent must be used within a ManualJAgentProvider'
    );
  }
  return context;
};
